import { useState } from "react";
import { invalidateExact, type QueryKey } from "./queryClient";

export interface UseMutationOptions<TArgs, TResult> {
  mutationFn: (args: TArgs) => Promise<TResult>;
  /** Exact record/list/overview keys to invalidate after success (design.md section 7). */
  invalidateKeys?: readonly QueryKey[];
}

export type MutationStatus = "idle" | "pending" | "success" | "error";

export interface UseMutationResult<TArgs, TResult> {
  mutate: (args: TArgs) => Promise<TResult>;
  status: MutationStatus;
  error: unknown;
  /**
   * Back to idle/no-error, with no mutationFn call (RDD review-reliability,
   * WARNING, R3-stale-error-on-reopen): a caller that re-shows this hook's
   * own `error` inside a modal needs a way to clear a past failed attempt
   * when the modal closes/reopens -- otherwise the PREVIOUS attempt's error
   * reappears before any new submit.
   *
   * Codex finding (CampaignDetailScreen, PR #384 round 5): clears `error`
   * and returns `status` to `idle` without touching any in-flight mutation.
   * A screen-scoped mutation hook instance stays mounted across a modal's
   * open/close cycle, so a caller must clear its stale result explicitly
   * (e.g. on modal close) instead of a fresh modal silently inheriting the
   * previous attempt's error.
   */
  reset: () => void;
}

/** Small typed mutation hook: pessimistic writes (design.md section 7) — callers await `mutate` and keep input local on failure; tracks pending/success/error and invalidates exact keys on success. */
export function useMutation<TArgs, TResult>(
  options: UseMutationOptions<TArgs, TResult>,
): UseMutationResult<TArgs, TResult> {
  const [state, setState] = useState<{ status: MutationStatus; error: unknown }>({
    status: "idle",
    error: undefined,
  });

  const mutate = async (args: TArgs): Promise<TResult> => {
    setState({ status: "pending", error: undefined });
    try {
      const result = await options.mutationFn(args);
      setState({ status: "success", error: undefined });
      if (options.invalidateKeys) {
        invalidateExact(options.invalidateKeys);
      }
      return result;
    } catch (error) {
      setState({ status: "error", error });
      throw error;
    }
  };

  const reset = () => setState({ status: "idle", error: undefined });

  return { mutate, status: state.status, error: state.error, reset };
}
